import React, { useEffect, useMemo, useRef, useState, useImperativeHandle } from 'react'

import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem } from '@material-ui/core'

import { CardNumberElement, CardExpiryElement, CardCvcElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'

import { loadStripe } from '@stripe/stripe-js'

import { showError } from '../../../actions/misc/misc.actions'

import { setPaymentMethod } from '../../../actions/households/households.actions'

import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useDispatch, useSelector } from 'react-redux'
import useCurrentUser from '../../../hooks/useCurrentUser'
import { canAccessFacility, isSuperUser } from '../../../utils/staffFacility'
import { USER_ROLES } from '../../../utils/enums'

const useStyles = makeStyles(() => ({
	title: {
		marginBottom: 24
	},
	dialogActions: {
		justifyContent: 'flex-start'
	},
	fieldGroup: {
		display: 'flex',
		flexWrap: 'wrap'
	},
	fieldWrapper: {
		minWidth: '400px !important',
		width: '400px !important',
		marginBottom: 20,
		'&:nth-child(2)': {
			marginLeft: '8px'
		},
		'& .MuiFilledInput-root': {
			minWidth: '400px !important',
			width: '400px !important'
		},
		'& .MuiTextField-root': {
			minWidth: '400px !important',
			width: '400px !important'
		}
	},
	double: {
		'& .field-wrapper': {
			minWidth: '196px !important',
			width: '196px !important'
		},
		'& .MuiFilledInput-root': {
			minWidth: '196px !important',
			width: '196px !important'
		},
		'& .MuiTextField-root': {
			minWidth: '196px !important',
			width: '196px !important'
		}
	},
	disableButton: {
		color: '#D02C2C',
		fontSize: '14px',
		lineHeight: '19px',
		borderRadius: '25px',
		marginBottom: 32,
		'&:hover': {
			backgroundColor: 'rgba(208, 44, 44, 0.2)'
		}
	}
}))

function StripeInput(props) {
	const { component: Component, inputRef, ...other } = props
	const elementRef = useRef()

	useImperativeHandle(inputRef, () => ({
		focus: () => elementRef.current.focus
	}))

	return <Component onReady={(element) => (elementRef.current = element)} {...other} />
}

const CardFields = ({ classes, saveRef, zipcode, setZipcode, onReady }) => {
	const stripe = useStripe()
	const elements = useElements()
	const [cardErrorMessage, setCardErrorMessage] = useState(null)
	const [expiryErrorMessage, setExpiryErrorMessage] = useState(null)
	const [cvvErrorMessage, setCvvErrorMessage] = useState(null)
	const [zipErrorMessage, setZipErrorMessage] = useState(null)

	useEffect(() => {
		onReady(!!stripe && !!elements)
	}, [stripe, elements, onReady])

	useEffect(() => {
		saveRef.current = () => {
			if (!stripe || !elements) return null

			const card = elements.getElement(CardNumberElement)
			const expiry = elements.getElement(CardExpiryElement)
			const cvv = elements.getElement(CardCvcElement)

			if (
				!card ||
				!expiry ||
				!cvv ||
				card._invalid ||
				expiry._invalid ||
				cvv._invalid ||
				card._empty ||
				expiry._empty ||
				cvv._empty ||
				!zipcode
			) {
				return { error: 'Fix form errors to continue.' }
			}

			return stripe.createToken(cvv, { address_zip: zipcode })
		}

		return () => {
			saveRef.current = null
		}
	}, [stripe, elements, zipcode, saveRef])

	const handleCardChange = ({ error }) => {
		error ? setCardErrorMessage(error.message) : setCardErrorMessage(null)
	}

	const handleExpiryChange = ({ error }) => {
		error ? setExpiryErrorMessage(error.message) : setExpiryErrorMessage(null)
	}

	const handleCvvChange = ({ error }) => {
		error ? setCvvErrorMessage(error.message) : setCvvErrorMessage(null)
	}

	const handleZipcode = (e) => {
		if (!e.currentTarget.contains(e.relatedTarget)) {
			if (e.target.value.length !== 5) {
				setZipErrorMessage('Your zipcode is not complete')
			} else {
				setZipErrorMessage(null)
			}
		}
	}

	const hasCardError = cardErrorMessage !== null
	const haseExpiryError = expiryErrorMessage !== null
	const hasCvvError = cvvErrorMessage !== null
	const hasZipError = zipErrorMessage !== null

	return (
		<>
			<div className={classes.fieldGroup}>
				<div className={classes.fieldWrapper}>
					<TextField
						variant="filled"
						fullWidth
						label="Card Number"
						error={hasCardError}
						helperText={hasCardError ? cardErrorMessage || 'Invalid Card' : ''}
						onChange={handleCardChange}
						InputLabelProps={{ shrink: true }}
						InputProps={{
							inputProps: { component: CardNumberElement },
							inputComponent: StripeInput
						}}
					/>
				</div>
			</div>
			<div className={clsx([classes.fieldGroup, classes.double])}>
				<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
					<TextField
						variant="filled"
						label="Exp. Date"
						error={haseExpiryError}
						helperText={haseExpiryError ? expiryErrorMessage || 'Invalid Expiration' : ''}
						onChange={handleExpiryChange}
						InputLabelProps={{ shrink: true }}
						InputProps={{
							inputProps: { component: CardExpiryElement },
							inputComponent: StripeInput
						}}
					/>
				</div>
				<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
					<TextField
						variant="filled"
						label="CVV"
						error={hasCvvError}
						helperText={hasCvvError ? cvvErrorMessage || 'Invalid Cvv' : ''}
						onChange={handleCvvChange}
						InputLabelProps={{ shrink: true }}
						InputProps={{
							inputProps: { component: CardCvcElement },
							inputComponent: StripeInput
						}}
					/>
				</div>
			</div>
			<div className={classes.fieldGroup}>
				<div className={classes.fieldWrapper}>
					<TextField
						variant="filled"
						fullWidth
						label="Zip Code"
						value={zipcode}
						error={hasZipError}
						helperText={hasZipError ? zipErrorMessage || 'Invalid Zipcode' : ''}
						onChange={(e) => setZipcode(e.target.value)}
						onBlur={handleZipcode}
					/>
				</div>
			</div>
		</>
	)
}

const PaymentMethodDialog = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const selected = useSelector((state) => state.specific.households.selected)
	const loading = useSelector((state) => state.specific.households.loading)
	const facilities = useSelector((state) => state.specific.facilities.facilities)
	const { user, facilityId: assignedFacilityId } = useCurrentUser()
	const roles = user?.roles || []
	const access = { roles, facilityId: assignedFacilityId }
	const canManageAnyFacility = isSuperUser(roles) || roles.includes(USER_ROLES.BackOfficeUser)

	const { isShown, setIsShown, onPaymentMethodUpdated, facilityId, lockFacility = false } = props

	const [selectedFacilityId, setSelectedFacilityId] = useState(facilityId || '')
	const [zipcode, setZipcode] = useState('')
	const [stripeReady, setStripeReady] = useState(false)
	const saveRef = useRef(null)

	const stripeFacilities = useMemo(() => {
		const withStripe = (facilities || []).filter(
			(facility) =>
				facility?.publishable_stripe_key &&
				facility.active !== false &&
				(canManageAnyFacility ||
					canAccessFacility(access, facility._id) ||
					String(facility._id) === String(facilityId))
		)

		return withStripe.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))
	}, [facilities, user, assignedFacilityId, canManageAnyFacility, facilityId])

	const selectedFacility = useMemo(() => {
		const match = (facility) => String(facility._id) === String(selectedFacilityId) && facility.publishable_stripe_key

		return stripeFacilities.find(match) || (facilities || []).find(match) || null
	}, [stripeFacilities, facilities, selectedFacilityId])

	const facilityOptions = useMemo(() => {
		if (selectedFacility && !stripeFacilities.some((facility) => String(facility._id) === String(selectedFacility._id))) {
			return [selectedFacility, ...stripeFacilities]
		}

		return stripeFacilities
	}, [selectedFacility, stripeFacilities])

	const stripePromise = useMemo(() => {
		if (!selectedFacility?.publishable_stripe_key) return null

		return loadStripe(selectedFacility.publishable_stripe_key)
	}, [selectedFacility?._id, selectedFacility?.publishable_stripe_key])

	useEffect(() => {
		if (!isShown) return

		setSelectedFacilityId(facilityId || '')
		setStripeReady(false)
	}, [isShown, facilityId])

	const handleFacilityChange = (event) => {
		setSelectedFacilityId(event.target.value)
		setStripeReady(false)
	}

	const saveCard = async () => {
		if (!selectedFacilityId) {
			dispatch(showError('Select a facility for this card.'))
			return
		}

		const token = await saveRef.current?.()

		if (!token) {
			dispatch(showError('Fix form errors to continue.'))
			return
		}

		if (token.error) {
			dispatch(showError(token.error.message || token.error))
			return
		}

		dispatch(setPaymentMethod(selected._id, token, selectedFacilityId)).then((updated) => {
			if (onPaymentMethodUpdated) {
				onPaymentMethodUpdated(updated)
			}
			setIsShown(false)
		})
	}

	return (
		<Dialog
			open={isShown}
			aria-labelledby="form-dialog-title"
			onClose={() => setIsShown(false)}
			PaperProps={{
				style: {
					minWidth: '448px',
					maxWidth: '448px',
					width: '448px'
				}
			}}>
			<DialogTitle className={classes.title}>ADD PAYMENT METHOD</DialogTitle>
			<DialogContent>
				<div className={classes.fieldGroup}>
					<div className={classes.fieldWrapper}>
						<TextField
							select
							variant="filled"
							fullWidth
							label="Facility"
							value={selectedFacility ? selectedFacilityId : ''}
							disabled={lockFacility}
							onChange={handleFacilityChange}
							InputLabelProps={{ shrink: true }}
							helperText="This card is saved on the client's default facility.">
							{facilityOptions.map((facility) => (
								<MenuItem key={facility._id} value={facility._id}>
									{facility.name}
								</MenuItem>
							))}
						</TextField>
					</div>
				</div>
				{stripePromise ? (
					<Elements key={selectedFacilityId || 'stripe'} stripe={stripePromise}>
						<CardFields
							classes={classes}
							saveRef={saveRef}
							zipcode={zipcode}
							setZipcode={setZipcode}
							onReady={setStripeReady}
						/>
					</Elements>
				) : null}
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button variant="contained" color="secondary" onClick={saveCard} disabled={loading || !stripeReady}>
					SAVE
				</Button>
				<Button variant="outlined" onClick={() => setIsShown(false)} color="secondary">
					CANCEL
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default PaymentMethodDialog
